import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { BoxFlex, Ribbon } from '@woovi/ui';
import { basesPointToPercentage, convertToMoney } from '@woovi/utils';

import PaymentCard from './PaymentCard.tsx';
import PaymentSectionChip from './PaymentSectionChip.tsx';
import type { PaymentActiveMethodInstallment_charge$key } from '../active/__generated__/PaymentActiveMethodInstallment_charge.graphql.ts';


type PaymentActiveMethodInstallmentProps = {
  charge: PaymentActiveMethodInstallment_charge$key;
};

const PaymentActiveMethodInstallment = (
  props: PaymentActiveMethodInstallmentProps,
): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();

  const charge = useFragment<PaymentActiveMethodInstallment_charge$key>(
    graphql`
      fragment PaymentActiveMethodInstallment_charge on Charge {
        valueWithDiscount
        availablePaymentMethods
        availablePaymentMethodsList {
          ... on PaymentMethodAvailableInstallments {
            installments {
              installmentNumber
              value
              discount
            }
          }
        }
      }
    `,
    props.charge,
  );

  const renderInstallment = (installment): ReactNode => {
    return (
      <PaymentCard
        value={installment.installmentNumber}
        installment={{
          number: installment.installmentNumber,
          value: convertToMoney(installment.value) ?? '',
        }}
        method={PAYMENT_METHOD_TYPE.PIX_CREDIARY}
      >
        <Stack spacing={2}>
          <Typography sx={{ fontSize: '16px', color: theme.palette.grey[400] }}>
            {t('Total')}: {convertToMoney(charge.valueWithDiscount)}
          </Typography>
          {installment.discount > 0 && (
            <Ribbon color={theme.palette.tertiary.main}>
              <Typography>🤑</Typography>
              <Typography color={'white'} sx={{ fontSize: '16px' }}>
                <b>{basesPointToPercentage(installment.discount)}</b>{' '}
                {t('of discount')}
              </Typography>
            </Ribbon>
          )}
        </Stack>
      </PaymentCard>
    );
  };

  return (
    <>
      <BoxFlex
        sx={{ position: 'relative', flexDirection: 'column', width: '100%' }}
      >
        <PaymentSectionChip>{t('Pix')}</PaymentSectionChip>
        {renderInstallment(
          charge?.availablePaymentMethodsList?.[0]?.installments[0],
        )}
      </BoxFlex>

      <BoxFlex
        sx={{ position: 'relative', flexDirection: 'column', width: '100%' }}
      >
        <PaymentSectionChip>{t('Pix Installment')}</PaymentSectionChip>
        {charge?.availablePaymentMethodsList?.[0]?.installments?.map(
          (installment, i) => {
            if (i === 0) {
              return null;
            }

            return renderInstallment(installment);
          },
        )}
      </BoxFlex>
    </>
  );
};

export default PaymentActiveMethodInstallment;
